import { OAuth2 } from '@backstage/core-app-api';
import { SignInPage } from '@backstage/core-components';
import {
  ApiBlueprint,
  BackstageIdentityApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
  configApiRef,
  createApiFactory,
  createApiRef,
  createFrontendModule,
  discoveryApiRef,
  oauthRequestApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import {
  SignInPageBlueprint,
  type SignInPageProps,
} from '@backstage/plugin-app-react';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import { signOutWithKeycloak } from './logout';

export const keycloakAuthApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({
  id: 'internal.auth.keycloak',
});

const keycloakAuthApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: keycloakAuthApiRef,
        deps: {
          configApi: configApiRef,
          discoveryApi: discoveryApiRef,
          oauthRequestApi: oauthRequestApiRef,
        },
        factory: ({ configApi, discoveryApi, oauthRequestApi }) => {
          const oauth = OAuth2.create({
            configApi,
            discoveryApi,
            environment: configApi.getOptionalString('auth.environment'),
            oauthRequestApi,
            provider: {
              id: 'oidc',
              title: 'ClearSlate Keycloak',
              icon: VpnKeyIcon,
            },
            defaultScopes: ['openid', 'profile', 'email'],
          });
          if (configApi.getOptionalString('clrslate.profile') === 'gcp') {
            const signOut = oauth.signOut.bind(oauth);
            oauth.signOut = () =>
              signOutWithKeycloak({
                getIdToken: () => oauth.getIdToken({ optional: true }),
                signOut,
                logoutUrl: configApi.getString('clrslate.logoutUrl'),
                baseUrl: configApi.getString('app.baseUrl'),
                redirect: url => window.location.assign(url),
              });
          }
          return oauth;
        },
      }),
    ),
});

const ConfiguredSignInPage = (props: SignInPageProps) => {
  const configApi = useApi(configApiRef);
  const authEnvironment = configApi.getOptionalString('auth.environment');

  if (authEnvironment !== 'production') {
    return <SignInPage {...props} providers={['guest']} />;
  }

  return (
    <SignInPage
      {...props}
      auto={configApi.getOptionalString('clrslate.profile') !== 'gcp'}
      provider={{
        id: 'keycloak',
        title: 'ClearSlate Keycloak',
        message: 'Sign in with your ClearSlate internal account',
        apiRef: keycloakAuthApiRef,
      }}
    />
  );
};

const keycloakSignInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => ConfiguredSignInPage,
  },
});

export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [keycloakAuthApi, keycloakSignInPage],
});
